import React from 'react';
import { Link } from 'react-router-dom';
import Sparkline from './Sparkline';
import { SectionHeading } from './DatasetCards';
import { PARTY_COLORS, fmtMoney } from '../api';
import { ArrowUpRight } from 'lucide-react';

export default function LedgerTable({ mps = [], meta, loading = false }) {
  const max = mps.reduce((m, x) => Math.max(m, x.total || 0), 0) || 1;

  return (
    <section className="bg-cream-100">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24">
        <SectionHeading
          kicker="Top of the ledger"
          title={<>The twelve who <span className="italic font-light">claimed the most.</span></>}
          subtitle={`Parliamentarians ranked by gross expenses claimed last quarter · ${meta?.latest_quarter || ''}. From the House of Commons Members\u2019 Expenditures Report.`}
          right={
            <Link to="/latest-quarter" className="inline-flex items-center gap-1.5 text-[13px] font-medium text-copper-600 hover:text-copper-500 transition">
              See all {meta?.total_mps || 343} →
            </Link>
          }
        />

        {/* Desktop table */}
        <div className="hidden md:block bg-cream-50 border border-ink-300/60 rounded-sm overflow-hidden">
          <div className="grid grid-cols-[60px_1.7fr_1fr_1.6fr_1.2fr_1fr] kicker text-ink-500 px-6 py-4 border-b border-ink-300/50 bg-cream-100/40">
            <div>Rank</div>
            <div>Parliamentarian</div>
            <div>Party · Province</div>
            <div>Largest discretionary line</div>
            <div>8-quarter trend</div>
            <div className="text-right">Claimed</div>
          </div>

          {(loading && mps.length === 0 ? Array.from({ length: 8 }) : mps).map((m, i) => (
            m ? (
              <Link
                key={m.slug}
                to={`/mp/${m.slug}`}
                className="group relative grid grid-cols-[60px_1.7fr_1fr_1.6fr_1.2fr_1fr] items-center px-6 py-5 border-b border-ink-300/40 last:border-0 hover:bg-cream-100/60 transition"
              >
                {/* Background bar */}
                <div className="absolute inset-y-0 left-0 bg-copper-50/0 group-hover:bg-copper-50/40 transition" style={{ width: `${((m.total || 0) / max) * 100}%`, opacity: 0.5 }} />
                <div className="relative font-serif text-[18px] text-ink-500 tabular">{String(m.rank).padStart(2, '0')}</div>
                <div className="relative">
                  <div className="text-[16px] text-ink-800 font-medium group-hover:text-copper-600 transition flex items-center gap-1.5">
                    {m.name}
                    <ArrowUpRight size={14} className="opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition" />
                  </div>
                  {m.constituency && <div className="text-[12px] text-ink-500 mt-0.5">{m.constituency}</div>}
                </div>
                <div className="relative flex items-center gap-2">
                  <span className="inline-block h-2 w-2 rounded-full" style={{ background: PARTY_COLORS[m.party] }} />
                  <span className="text-[12px] tracking-wider uppercase text-ink-700 font-medium">{m.party}</span>
                  <span className="text-ink-300">·</span>
                  <span className="text-[12px] uppercase tracking-wider text-ink-500">{m.province || '—'}</span>
                </div>
                <div className="relative">
                  <div className="text-[14px] text-ink-700">{m.line}</div>
                  <div className="text-[12px] text-ink-500 tabular">{fmtMoney(m.line_amount)}</div>
                </div>
                <div className="relative">
                  <Sparkline data={(m.trend || []).filter(v => v > 0)} width={140} height={34} />
                </div>
                <div className="relative text-right">
                  <div className="font-serif text-[22px] text-ink-800 tabular tracking-tight">{fmtMoney(m.total)}</div>
                  <div className="text-[11px] text-ink-500 mt-0.5">disc. {fmtMoney(m.discretionary)}</div>
                </div>
              </Link>
            ) : (
              <div key={i} className="grid grid-cols-[60px_1.7fr_1fr_1.6fr_1.2fr_1fr] items-center px-6 py-5 border-b border-ink-300/40 animate-pulse">
                <div className="h-4 w-6 bg-ink-200" />
                <div className="h-4 w-44 bg-ink-200" />
                <div className="h-3 w-24 bg-ink-200" />
                <div className="h-3 w-32 bg-ink-200" />
                <div className="h-4 w-32 bg-ink-200" />
                <div className="h-5 w-20 bg-ink-200 justify-self-end" />
              </div>
            )
          ))}
        </div>

        {/* Mobile cards */}
        <div className="md:hidden space-y-3">
          {mps.map((m) => (
            <Link key={m.slug} to={`/mp/${m.slug}`} className="block bg-cream-50 border border-ink-300/60 rounded-sm p-5">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-[11px] text-ink-500 tabular">#{String(m.rank).padStart(2,'0')}</div>
                  <div className="text-[16px] font-medium text-ink-800 mt-0.5">{m.name}</div>
                  <div className="text-[11px] uppercase tracking-wider text-ink-500 mt-1 flex items-center gap-1.5">
                    <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: PARTY_COLORS[m.party] }} />
                    {m.party} · {m.province || '—'}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-serif text-[20px] font-semibold text-ink-800 tabular">{fmtMoney(m.total)}</div>
                  <div className="text-[11px] text-ink-500">disc. {fmtMoney(m.discretionary)}</div>
                </div>
              </div>
              <div className="mt-3 text-[13px] text-ink-700">
                {m.line} <span className="text-ink-500">· {fmtMoney(m.line_amount)}</span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
